import { useRef } from 'react';
import { useDrop } from 'react-dnd';

import { comparePaths } from '../../utils/compare-paths.util';
import { isParent } from '../../utils/is-parent.util';
import { useDocumentEditorContext } from '../../contexts/DocumentEditorContext/hooks/useDocumentEditorContext.hook';
import type { EnrichedSection } from '../../types/enriched-section.type';
import type { Editable } from '../../types/editable.type';

import { ItemTypes } from './utils/item-types.util';

interface Props {
  readonly isVisible: boolean;
  readonly targetSectionRealPath: readonly number[];
  readonly targetSectionInsertionPointRealPath?: readonly number[];
}

export const DropZone = ({
  isVisible,
  targetSectionRealPath,
  targetSectionInsertionPointRealPath,
}: Readonly<Props>) => {
  const { actions } = useDocumentEditorContext<Editable>();

  //----------------------------------------------------------------
  // 1. useDrop hook
  //----------------------------------------------------------------
  const [{ isOverCurrent }, dropRef] = useDrop<
    EnrichedSection,
    unknown,
    Readonly<{ isOverCurrent: boolean; canDrop: boolean }>
  >(
    () => ({
      accept: ItemTypes.SECTION,
      // DropZone beeing displayed means that the item can be dropped
      canDrop: () => isVisible,
      drop(section: Readonly<EnrichedSection>, monitor) {
        if (
          comparePaths(section.realPath, targetSectionRealPath) === 0 ||
          isParent(section.realPath, targetSectionRealPath)
        ) {
          return;
        }
        const didDrop = monitor.didDrop();
        if (didDrop) {
          return;
        }
        actions.move(
          section.realPath,
          targetSectionRealPath,
          section.insertionPoint?.realPath,
          targetSectionInsertionPointRealPath,
        );
      },
      collect: (monitor) => ({
        isOverCurrent: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
      }),
    }),
    [isVisible, targetSectionRealPath, targetSectionInsertionPointRealPath],
  );

  const ref = useRef<HTMLDivElement | null>(null);

  // TODO : check if we can refactor this code in order to respect this rule
  // eslint-disable-next-line react-hooks/refs
  dropRef(ref);

  return (
    <div
      className="empty"
      ref={ref}
      style={{
        background: isVisible && isOverCurrent ? 'lightgray' : 'transparent',
        width: '100%',
        height: '8px',
        borderRadius: '2px',
        marginTop: '2px',
        marginBottom: '2px',
      }}
    />
  );
};
